import { useEffect, useState, useCallback } from "react";
import { osApi, formatINR, timeAgo, stageColor } from "./osApi";
import { toast } from "sonner";
import OSLeadDrawer from "./OSLeadDrawer";
import { AlertCircle, Bell, Flame } from "lucide-react";

export default function OSFollowups() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("today");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Get a big page and slice client-side
      const res = await osApi.listLeads({ sort: "followup_asc", page_size: 100, filter: "followup" });
      setItems(res.items);
    } catch { toast.error("Failed"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const overdue = items.filter(l => l.next_followup && new Date(l.next_followup) < now);
  const today = items.filter(l => {
    if (!l.next_followup) return false;
    const d = new Date(l.next_followup);
    return d.toDateString() === now.toDateString();
  });
  const priority = items.filter(l => l.lead_status === "Hot");

  const tabs = [
    { key: "today", label: "Today", icon: Bell, list: today },
    { key: "overdue", label: "Overdue", icon: AlertCircle, list: overdue },
    { key: "priority", label: "Priority", icon: Flame, list: priority },
  ];
  const active = tabs.find(t => t.key === tab);

  return (
    <div className="space-y-5" data-testid="os-followups-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Follow-up Engine</h1>
        <p className="text-sm mt-1" style={{color:"var(--os-text-muted)"}}>Every conversation should have a next step. Never lose a lead.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="os-btn-ghost"
            style={tab === t.key ? {borderColor:"var(--os-accent)", color:"var(--os-text)", background:"rgba(37,99,235,0.1)"} : {}}
            data-testid={`os-followup-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label} · {t.list.length}
          </button>
        ))}
      </div>

      <div className="os-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="os-table">
            <thead><tr><th>Lead ID</th><th>Customer</th><th>Budget</th><th>Stage</th><th>Follow-up</th><th>Agent</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-10" style={{color:"var(--os-text-muted)"}}>Loading…</td></tr>}
              {!loading && active.list.length === 0 && <tr><td colSpan={6} className="text-center py-10" style={{color:"var(--os-text-muted)"}}>Nothing pending.</td></tr>}
              {active.list.map(l => (
                <tr key={l.id} onClick={() => setSelected(l.id)} data-testid={`os-followup-row-${l.lead_id}`}>
                  <td className="os-mono text-xs" style={{color:"var(--os-text-muted)"}}>{l.lead_id}</td>
                  <td className="font-medium">{l.customer_name}<div className="text-[11px]" style={{color:"var(--os-text-dim)"}}>{l.phone}</div></td>
                  <td className="os-mono">{formatINR(l.budget)}</td>
                  <td><span className={`os-badge ${stageColor(l.stage)}`}>{l.stage}</span></td>
                  <td className="text-xs">{l.next_followup ? new Date(l.next_followup).toLocaleString() : "—"}</td>
                  <td className="text-xs">{l.assigned_agent_name || <span style={{color:"var(--os-text-dim)"}}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <OSLeadDrawer leadId={selected} onClose={() => setSelected(null)} onUpdate={load} />}
    </div>
  );
}
